import React, { useEffect, useState } from 'react';
import { BadgeCheck, ScrollText } from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../api';

/**
 * Badge + mention légale indiquant la conformité au programme officiel ETM.
 *
 * Sources officielles :
 * - Arrêté du 29 novembre 2019 (programme officiel de l'ETM)
 * - Code de la route (Légifrance)
 * - REMC (Référentiel pour l'Éducation à la Mobilité Citoyenne, arrêté 23/04/2012)
 *
 * Mention indépendance : Code 2 Roues n'est pas affilié aux éditions Rousseau.
 */

export function OfficialBadge({ size = 'md', className }) {
  const sizes = {
    sm: 'text-[9px] px-2 py-0.5 gap-1',
    md: 'text-[10px] px-2.5 py-1 gap-1.5',
    lg: 'text-xs px-3 py-1.5 gap-2',
  };
  const iconSizes = { sm: 'w-3 h-3', md: 'w-3.5 h-3.5', lg: 'w-4 h-4' };

  return (
    <span
      data-testid="official-badge"
      className={cn(
        'inline-flex items-center rounded-full font-black uppercase tracking-widest',
        'bg-[#16a34a]/12 text-[#16a34a] border border-[#16a34a]/25',
        sizes[size],
        className
      )}
      title="Contenu pédagogique aligné sur le programme officiel défini par l'arrêté du 29 novembre 2019"
    >
      <BadgeCheck className={iconSizes[size]} />
      Aligné sur le programme officiel 2026
    </span>
  );
}

export function OfficialLegalNotice({ className, variant = 'default' }) {
  const [stats, setStats] = useState({ questions: 719, modules: 38 });
  useEffect(() => {
    api.getPublicStats().then(setStats).catch(() => {});
  }, []);

  if (variant === 'compact') {
    return (
      <p
        data-testid="official-legal-notice-compact"
        className={cn(
          'text-[10px] text-muted-foreground leading-relaxed text-center',
          className
        )}
      >
        Questions originales conformes au programme officiel de l'ETM (arrêté du 29 novembre 2019).
        Code 2 Roues est une plateforme indépendante, non affiliée aux éditions Rousseau®.
      </p>
    );
  }

  return (
    <div
      data-testid="official-legal-notice"
      className={cn(
        'mc-card p-4 sm:p-5 border-l-4 border-l-[#16a34a]',
        className
      )}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#16a34a]/12 text-[#16a34a]">
          <ScrollText className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <OfficialBadge size="sm" />
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              Mentions légales & sources
            </span>
          </div>
          <p className="text-xs sm:text-[13px] text-foreground/85 leading-relaxed">
            Les <strong>{stats.questions} questions</strong> et <strong>{stats.modules} modules pédagogiques</strong> proposés sur
            Code 2 Roues sont <strong>rédigés de manière originale</strong> par l'équipe <strong>CODE 2 ROUES</strong>
            et son réseau d'enseignants ECSR validés,
            en stricte conformité avec le programme officiel de l'<strong>Épreuve Théorique Moto (ETM)</strong>
            — arrêté du <strong>29 novembre 2019 modifié</strong>, en vigueur depuis le 1er mars 2020.
          </p>
          <p className="text-xs sm:text-[13px] text-foreground/85 leading-relaxed mt-2">
            Sources officielles mobilisées :
          </p>
          <ul className="text-xs sm:text-[13px] text-foreground/80 leading-relaxed mt-1 ml-4 list-disc space-y-0.5">
            <li><strong>Code de la route</strong> (articles L. et R. — Légifrance)</li>
            <li><strong>Programme officiel de l'ETM</strong> (Sécurité routière)</li>
            <li><strong>REMC</strong> — Référentiel pour l'Éducation à la Mobilité Citoyenne (arrêté du 23 avril 2012)</li>
            <li><strong>Bulletins de la Sécurité routière</strong> (ONISR)</li>
          </ul>
          <p className="text-[11px] text-muted-foreground italic mt-3 leading-relaxed">
            Code 2 Roues est une plateforme <strong>indépendante</strong>, éditée par un
            enseignant de la Conduite et de la Sécurité Routière (ECSR).
            Elle n'est <strong>pas affiliée</strong> aux éditions Rousseau®, à ENPC® ou à toute autre marque
            d'édition du code de la route. Les marques citées restent la propriété de leurs détenteurs respectifs.
            L'identité complète de l'éditeur est précisée dans les <a href="/mentions-legales" className="underline hover:text-[hsl(var(--primary))]">mentions légales</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
